import type { StreamEntry } from "./state.svelte.js";

const TURNS_KEPT = 8; // a late row belongs to one of the last few turns

// Does a row of this turn sit below the prompt: true (on screen at its start), false (on screen
// only at a later turn's start), undefined (no proof: the time rule decides).
export type PromptVerdict = (promptEntryId: string) => boolean | undefined;

// The daemon stamps each live row with its turn (attachManagedTurnIdentity): seated by turn, not two clocks.
export class TurnPrompts {
  private readonly byAgent = new Map<string, Array<{ turnId: string; prompts: Set<string> }>>();

  noteStarted(agentId: string, turnId: string | undefined, entries: readonly StreamEntry[]): void {
    const turns = this.byAgent.get(agentId) ?? [];
    if (!turnId || turns.some((t) => t.turnId === turnId)) return; // a repeat keeps the first record
    const prompts = entries.filter(
      (e) => e.kind === "user_message" && e.seq === undefined && !e.rows?.length,
    );
    turns.push({ turnId, prompts: new Set(prompts.map((e) => e.id)) });
    this.byAgent.set(agentId, turns.slice(-TURNS_KEPT));
  }

  verdictFor(agentId: string, turnId: string | undefined): PromptVerdict | undefined {
    const turns = this.byAgent.get(agentId) ?? [];
    const at = turns.findIndex((t) => t.turnId === turnId);
    if (at < 0) return undefined; // undefined: no turn identity, or its start was not seen
    const own = turns[at].prompts;
    const later = turns.slice(at + 1);
    return (id) => {
      if (own.has(id)) return true;
      return later.some((t) => t.prompts.has(id)) ? false : undefined;
    };
  }

  clear(agentId?: string): void {
    if (agentId === undefined) this.byAgent.clear();
    else this.byAgent.delete(agentId);
  }
}
